/**
 * Client-side DRAFT state for the cafeteria Confirm-deliveries surface.
 *
 * The received-quantity edits (`qty`) and per-line delivery temperatures
 * (`temp`) are local until the deliveries are confirmed (committed
 * server-side via receive-delivery). This store persists that uncommitted
 * draft to localStorage so "Save for later" survives navigating away and
 * back — keyed by (school location × service date). A draft is dropped once
 * both maps are empty (e.g. after a successful confirm clears them), and
 * drafts older than `RECEIVE_DRAFT_MAX_AGE_DAYS` are pruned so abandoned ones
 * don't pile up.
 *
 * Manual localStorage sync (no zustand persist middleware), mirroring the
 * serve-draft store.
 */

import { create } from 'zustand'

const DRAFTS_KEY = 'ordo.cafeteria.receiveDrafts'
const RECEIVE_DRAFT_MAX_AGE_DAYS = 3
const DAY_MS = 24 * 60 * 60 * 1000

/** One confirm-deliveries session's uncommitted edits, plus a stamp for pruning. */
export interface ReceiveDraft {
  qty: Record<string, number>
  temp: Record<string, number | null>
  /**
   * The "Time reading" box (`HH:MM`, PROD-1279 D9). Optional because drafts
   * persisted before this shipped have no such key; `getDraft` normalises it
   * to `''` so the flow never reads `undefined` into a controlled input.
   */
  readingTime?: string
  updatedAt: number
}

/** What a session's draft looks like to the flow: the persisted edits, no stamp. */
export type ReceiveDraftEdits = Pick<ReceiveDraft, 'qty' | 'temp'> & { readingTime?: string }

/** Stable key for a receive draft: a school location's deliveries for one date. */
export function receiveDraftKey(locationId: string, serviceDate: string): string {
  return `${locationId}::${serviceDate}`
}

function read<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') {
    return fallback
  }
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown) {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // localStorage unavailable / quota — non-fatal.
  }
}

/** True when a draft holds no uncommitted edits. Pure + tested. */
export function isReceiveDraftEmpty(draft: ReceiveDraftEdits): boolean {
  return Object.keys(draft.qty).length === 0 && Object.keys(draft.temp).length === 0 && (draft.readingTime ?? '') === ''
}

/** Drop drafts last touched before `now - maxAgeDays`. Pure + tested. */
export function pruneReceiveDrafts(
  drafts: Record<string, ReceiveDraft>,
  now: number,
  maxAgeDays: number = RECEIVE_DRAFT_MAX_AGE_DAYS,
): Record<string, ReceiveDraft> {
  const cutoff = now - maxAgeDays * DAY_MS
  let changed = false
  const next: Record<string, ReceiveDraft> = {}
  for (const [key, draft] of Object.entries(drafts)) {
    if (draft.updatedAt >= cutoff) {
      next[key] = draft
    } else {
      changed = true
    }
  }
  return changed ? next : drafts
}

interface ReceiveDraftStore {
  drafts: Record<string, ReceiveDraft>
  /** Read a session's draft (empty maps when none persisted). */
  getDraft: (key: string) => Required<ReceiveDraftEdits>
  /** Persist a session's draft. Removes the entry when it holds nothing. */
  saveDraft: (key: string, draft: ReceiveDraftEdits) => void
  /** Drop a session's draft (after the deliveries are confirmed). */
  clearDraft: (key: string) => void
}

export const useCafeteriaReceiveDraftStore = create<ReceiveDraftStore>((set, get) => ({
  drafts: read<Record<string, ReceiveDraft>>(DRAFTS_KEY, {}),
  getDraft: (key) => {
    const draft = get().drafts[key]
    return {
      qty: draft?.qty ?? {},
      temp: draft?.temp ?? {},
      readingTime: draft?.readingTime ?? '',
    }
  },
  saveDraft: (key, draft) => {
    if (isReceiveDraftEmpty(draft)) {
      get().clearDraft(key)
      return
    }
    const now = Date.now()
    const pruned = pruneReceiveDrafts(get().drafts, now)
    const drafts = {
      ...pruned,
      [key]: { qty: draft.qty, temp: draft.temp, readingTime: draft.readingTime ?? '', updatedAt: now },
    }
    write(DRAFTS_KEY, drafts)
    set({ drafts })
  },
  clearDraft: (key) => {
    if (!get().drafts[key]) {
      return
    }
    const drafts = { ...get().drafts }
    delete drafts[key]
    write(DRAFTS_KEY, drafts)
    set({ drafts })
  },
}))
